import { Injectable } from "@angular/core";
import {
  ExportContext,
  ExportFileFormat,
  ExportRange,
  ExportService,
  ExportTaskProcessor,
} from "app/layout/export-overlay";
import ceil from "lodash-es/ceil";
import get from "lodash-es/get";
import { LostSaleService } from "../../lost-sale/lost-sale.service";
import { LostSaleItem, LostSaleSummaryItem, LostSaleQueryParams } from "../models/lost-sale.interfaces";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";

const LOST_SALE_V2_COMMERCIAL_EXPORT_ID = "lost-sale-v2-commercial";
const LOST_SALE_V2_COMMERCIAL_TITLE = "Venta Perdida Comercial";
const LOST_SALE_V2_SUMMARY_EXPORT_ID = "lost-sale-v2-summary";
const LOST_SALE_V2_SUMMARY_TITLE = "Resumen Venta Perdida";
const LOCALIZED_COMMERCIAL_PROPERTIES_V2 = {
  trip: "Viaje",
  route: "Ruta",
  user: "Usuario",
  date: "Fecha",
  materialName: "Material",
  materialCode: "Código Material",
  materialGroupName: "Grupo Material",
  customerName: "Cliente",
  customerCode: "Código Cliente",
  numberOrder: "Pedido",
  invoiceDocument: "Factura",
  sellerToName: "Responsable",
  locationTwo: "Ubicación",
  ordersCount: "# Pedidos",
  totalSoldOriginalQuantityUMBUnit: "Unidad de medición Cantidad Deseada UMB",
  totalSoldOriginalQuantityUMB: "Cantidad Deseada UMB",
  totalSoldOriginalQuantityUMPUnit: "Unidad de medición Cantidad Deseada UMP",
  totalSoldOriginalQuantityUMP: "Cantidad Deseada UMP",
  totalSoldQuantityUMBUnit: "Unidad de medición Cantidad Vendida UMB",
  totalSoldQuantityUMB: "Cantidad Vendida UMB",
  totalSoldQuantityUMPUnit: "Unidad de medición Cantidad Vendida UMP",
  totalSoldQuantityUMP: "Cantidad Vendida UMP",
  totalConfirmedQuantityUMBUnit: "Unidad de medición Cantidad Confirmada UMB",
  totalConfirmedQuantityUMB: "Cantidad Confirmada UMB",
  totalConfirmedQuantityUMPUnit: "Unidad de medición Cantidad Confirmada UMP",
  totalConfirmedQuantityUMP: "Cantidad Confirmada UMP",
  totalDeliveredQuantityUMBUnit: "Unidad de medición Cantidad Entregada UMB",
  totalDeliveredQuantityUMB: "Cantidad Entregada UMB",
  totalDeliveredQuantityUMPUnit: "Unidad de medición Cantidad Entregada UMP",
  totalDeliveredQuantityUMP: "Cantidad Entregada UMP",
  totalInvoicedQuantityUMBUnit: "Unidad de medición Cantidad Facturada UMB",
  totalInvoicedQuantityUMB: "Cantidad Facturada UMB",
  totalInvoicedQuantityUMPUnit: "Unidad de medición Cantidad Facturada UMP",
  totalInvoicedQuantityUMP: "Cantidad Facturada UMP",
  commercialLostSaleUMBUnit: "Unidad de medición Comercial UMB",
  commercialLostSaleUMB: "Comercial UMB",
  commercialLostSaleUMPUnit: "Unidad de medición Comercial UMP",
  commercialLostSaleUMP: "Comercial UMP",
  logisticLostSaleUMBUnit: "Unidad de medición Logística UMB",  
  logisticLostSaleUMB: "Logística UMB",  
  logisticLostSaleUMPUnit: "Unidad de medición Logística UMP",
  logisticLostSaleUMP: "Logística UMP",
  realLostSaleUMBUnit: "Unidad de medición Real UMB",
  realLostSaleUMB: "Real UMB",
  realLostSaleUMPUnit: "Unidad de mediciónReal UMP",
  realLostSaleUMP: "Real UMP",
  sumSoldQuantity: "Suma Pedido",
  sumStockConfirmedERP: "Suma Reserva",
  sumDeliveredQuantity: "Suma Desp",
  sumInvoiceQuantity: "Suma Factura",
  lostSaleAmount: "Diferencia",  
  commercialLostSalePercentage: "% Comercial",
  logisticLostSalePercentage: "% Logística",
  realLostSalePercentage: "% Real"
};

const LOCALIZED_SUMMARY_PROPERTIES_V2 = {
  materialName: "Material",
  materialCode: "Código Material",
  materialGroupName: "Grupo Material",
  customerName: "Cliente",
  customerCode: "Código Cliente",
  numberOrder: "Pedido",
  invoiceDocument: "Factura",
  sellerToName: "Responsable",
  locationTwo: "Ubicación",
  ordersCount: "Número Pedidos",
  totalSoldOriginalQuantityUMBUnit: "Unidad de medición Total Deseada UMB",
  totalSoldOriginalQuantityUMB: "Total Deseada UMB",
  totalSoldOriginalQuantityUMPUnit: "Unidad de medición Total Deseada UMP",
  totalSoldOriginalQuantityUMP: "Total Deseada UMP",
  totalSoldQuantityUMBUnit: "Unidad de medición Total Vendida UMB",
  totalSoldQuantityUMB: "Total Vendida UMB",
  totalSoldQuantityUMPUnit: "Unidad de medición Total Vendida UMP",
  totalSoldQuantityUMP: "Total Vendida UMP",
  totalConfirmedQuantityUMBUnit: "Unidad de medición Total Confirmada UMB",
  totalConfirmedQuantityUMB: "Total Confirmada UMB",
  totalConfirmedQuantityUMPUnit: "Unidad de medición Total Confirmada UMP",
  totalConfirmedQuantityUMP: "Total Confirmada UMP",
  totalDeliveredQuantityUMBUnit: "Unidad de medición Total Entregada UMB",
  totalDeliveredQuantityUMB: "Total Entregada UMB",
  totalDeliveredQuantityUMPUnit: "Unidad de medición Total Entregada UMP",
  totalDeliveredQuantityUMP: "Total Entregada UMP",
  totalInvoicedQuantityUMBUnit: "Unidad de medición Total Facturada UMB",
  totalInvoicedQuantityUMB: "Total Facturada UMB",
  totalInvoicedQuantityUMPUnit: "Unidad de medición Total Facturada UMP",
  totalInvoicedQuantityUMP: "Total Facturada UMP",
  commercialLostSaleUMBUnit: "Unidad de medición Comercial UMB",
  commercialLostSaleUMB: "Comercial UMB",
  commercialLostSaleUMPUnit: "Unidad de medición Comercial UMP",
  commercialLostSaleUMP: "Comercial UMP",
  logisticLostSaleUMBUnit: "Unidad de medición Logística UMB",
  logisticLostSaleUMB: "Logística UMB",
  logisticLostSaleUMPUnit: "Unidad de medición Logística UMP",
  logisticLostSaleUMP: "Logística UMP",
  realLostSaleUMBUnit: "Unidad de medición Real UMB",
  realLostSaleUMB: "Real UMB",
  realLostSaleUMPUnit: "Unidad de medición Real UMP",
  realLostSaleUMP: "Real UMP",
  sumSoldQuantity: "Suma Pedido ",
  sumStockConfirmedERP: "Suma Reserva",
  sumDeliveredQuantity: "Suma Desp",
  sumInvoiceQuantity: "Suma Factura",
  lostSaleAmount: "Diferencia",
  commercialLostSalePercentage: "% Comercial",
  logisticLostSalePercentage: "% VP Logística",
  realLostSalePercentage: "% VP Real"
};

@Injectable()
export class LostSaleV2Export {
  constructor(
    private exportService: ExportService,
    private lostSaleService: LostSaleService,
    private companyCurrencyPipe: CompanyCurrencyPipe
  ) {}

  exportCommercial(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    params: LostSaleQueryParams,
    data: LostSaleItem[]
  ): void {
    const totalPages = ceil(totalItems / pageSize) + 1;
    const task = this.exportService.task({
      id: LOST_SALE_V2_COMMERCIAL_EXPORT_ID,
      title: LOST_SALE_V2_COMMERCIAL_TITLE,
      format,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (context: ExportContext) => {
        context.processor.localizeItemProperties(
          LOST_SALE_V2_COMMERCIAL_TITLE,
          LOCALIZED_COMMERCIAL_PROPERTIES_V2
        );
      },
      next: (context) => {
        if (range === ExportRange.AllPages) {
          this.fetchCommercialData(params, context.iteration - 1, pageSize).then(items => {
            const transformedData = this.transformCommercialItem(items);
            context.add(transformedData);
            context.next();
          }).catch(() => {
            context.next();
          });
        } else {
          const transformedData = this.transformCommercialItem(data);
          context.add(transformedData);
          context.next();
        }
      }
    });

    if (task) {
      task.start();
    }
  }

  exportSummary(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    params: LostSaleQueryParams,
    data: LostSaleSummaryItem[]
  ): void {
    const totalPages = ceil(totalItems / pageSize) + 1;
    const localizedSummaryProps = this.getLocalizedSummaryProps();

    const task = this.exportService.task({
      id: LOST_SALE_V2_SUMMARY_EXPORT_ID,
      title: LOST_SALE_V2_SUMMARY_TITLE,
      format,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (context: ExportContext) => {
        context.processor.localizeItemProperties(
          LOST_SALE_V2_SUMMARY_TITLE,
          localizedSummaryProps
        );
      },
      next: (context) => {
        if (range === ExportRange.AllPages) {
          this.fetchSummaryData(params, context.iteration - 1, pageSize).then(items => {
            const transformedData = this.transformSummaryItem(items);
            context.add(transformedData);
            context.next();
          }).catch(() => {
            context.next();
          });
        } else {
          const transformedData = this.transformSummaryItem(data);
          context.add(transformedData);
          context.next();
        }
      }
    });

    if (task) {
      task.start();
    }
  }

  private async fetchCommercialData(
    params: LostSaleQueryParams,
    pageIndex: number,
    pageSize: number
  ): Promise<LostSaleItem[]> {
    const queryParams = {
      ...params,
      page: pageIndex + 1,
      pageSize
    };

    try {
      const response = await this.lostSaleService.exportLostSaleV2(queryParams, 'export').toPromise();
      return response?.data[0].data || [];
    } catch (error) {
      return [];
    }
  }

  private async fetchSummaryData(
    params: LostSaleQueryParams,
    pageIndex: number,
    pageSize: number
  ): Promise<LostSaleSummaryItem[]> {
    const queryParams = {
      ...params,
      page: pageIndex + 1,
      pageSize
    };

    try {
      const response = await this.lostSaleService.exportLostSaleSummaryV2(queryParams, 'export').toPromise();
      return response?.data[0].data || [];
    } catch (error) {
      return [];
    }
  }

  private transformCommercialItem(items): any[] {
    return items.map(item => {
      const backendVP = (item as any).backendCalculatedVP;

      return {
        trip: item.trip?.id || item.tripId || '',
        route: `${item.route?.name || item.subRoute?.name || '-'}`,
        user: `${item.user || '-'}`,
        date: ExportTaskProcessor.types.excel.datetime(item.createdAt),
        materialName: item.material?.name || item.materialName || '',
        materialCode: item.material?.code || item.materialCode || '',
        materialGroupName: item.material?.groupName || item.materialGroupName || '',
        customerName: item.customer?.name || item.customerName || '',
        customerCode: item.customer?.code || item.customerCode || '',
        numberOrder: item.serials || item.documentNumber || '',
        invoiceDocument: '-',
        sellerToName: item.user || '',
        locationTwo: '-',
        ordersCount: ExportTaskProcessor.types.number(1),

        totalSoldOriginalQuantityUMBUnit: item?.unitOfMeasure?.base?.name,     
        totalSoldOriginalQuantityUMB: ExportTaskProcessor.types.number(item.soldOriginalQuantity || 0),
        totalSoldOriginalQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        totalSoldOriginalQuantityUMP: ExportTaskProcessor.types.number(item.parallelOriginalQuantity || 0),
        totalSoldQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
        totalSoldQuantityUMB: ExportTaskProcessor.types.number(item.baseQuantity || 0),
        totalSoldQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        totalSoldQuantityUMP: ExportTaskProcessor.types.number(item.parallelQuantity || 0),
        totalConfirmedQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
        totalConfirmedQuantityUMB: ExportTaskProcessor.types.number(item.stockConfirmedBase || 0),
        totalConfirmedQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        totalConfirmedQuantityUMP: ExportTaskProcessor.types.number(item.stockConfirmedParallel || 0),
        totalDeliveredQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
        totalDeliveredQuantityUMB: ExportTaskProcessor.types.number(item.deliveredBaseQuantity || 0),
        totalDeliveredQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        totalDeliveredQuantityUMP: ExportTaskProcessor.types.number(item.deliveredParallelQuantity || 0),
        totalInvoicedQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
        totalInvoicedQuantityUMB: ExportTaskProcessor.types.number(item.invoicedBaseQuantity || 0),
        totalInvoicedQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        totalInvoicedQuantityUMP: ExportTaskProcessor.types.number(item.invoicedParallelQuantity || 0),

        commercialLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
        commercialLostSaleUMB: ExportTaskProcessor.types.number(backendVP?.commercialLostSaleUMB || 0),
        commercialLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        commercialLostSaleUMP: ExportTaskProcessor.types.number(backendVP?.commercialLostSaleUMP || 0),
        logisticLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
        logisticLostSaleUMB: ExportTaskProcessor.types.number(backendVP?.logisticLostSaleUMB || 0),
        logisticLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        logisticLostSaleUMP: ExportTaskProcessor.types.number(backendVP?.logisticLostSaleUMP || 0),
        realLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
        realLostSaleUMB: ExportTaskProcessor.types.number(backendVP?.realLostSaleUMB || 0),
        realLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
        realLostSaleUMP: ExportTaskProcessor.types.number(backendVP?.realLostSaleUMP || 0),

        sumSoldQuantity: ExportTaskProcessor.types.number(0),
        sumStockConfirmedERP: ExportTaskProcessor.types.number(0),
        sumDeliveredQuantity: ExportTaskProcessor.types.number(0),
        sumInvoiceQuantity: ExportTaskProcessor.types.number(0),
        lostSaleAmount: ExportTaskProcessor.types.number(0),        

        commercialLostSalePercentage: ExportTaskProcessor.types.number(
          item.soldOriginalQuantity > 0
            ? ((backendVP?.commercialLostSaleUMB || 0) / item.soldOriginalQuantity) * 100
            : 0
        ),
        logisticLostSalePercentage: ExportTaskProcessor.types.number(
          item.baseQuantity > 0
            ? ((backendVP?.logisticLostSaleUMB || 0) / item.baseQuantity) * 100
            : 0
        ),
        realLostSalePercentage: ExportTaskProcessor.types.number(
          item.baseQuantity > 0
            ? ((backendVP?.realLostSaleUMB || 0) / item.baseQuantity) * 100
            : 0
        ),
      };
    });
  }

  private transformSummaryItem(items: LostSaleSummaryItem[]): any[] {
    return items.map(item => ({
      materialName: item.materialName || '',
      materialCode: item.materialCode || '',
      materialGroupName: item.materialGroupName || '',
      customerName: item.customerName || '',
      customerCode: item.customerCode || '',
      numberOrder: item.numberOrder || '',
      invoiceDocument: item.invoiceDocument || '',
      sellerToName: item.sellerToName || '',
      locationTwo: item.locationTwo || '',
      ordersCount: ExportTaskProcessor.types.number(item.ordersCount || 0),

      totalSoldOriginalQuantityUMBUnit: item?.unitOfMeasure?.base?.name,   
      totalSoldOriginalQuantityUMB: ExportTaskProcessor.types.number(item.totalSoldOriginalQuantityUMB || 0),
      totalSoldOriginalQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      totalSoldOriginalQuantityUMP: ExportTaskProcessor.types.number(item.totalSoldOriginalQuantityUMP || 0),
      totalSoldQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
      totalSoldQuantityUMB: ExportTaskProcessor.types.number(item.totalSoldQuantityUMB || 0),
      totalSoldQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      totalSoldQuantityUMP: ExportTaskProcessor.types.number(item.totalSoldQuantityUMP || 0),
      totalConfirmedQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
      totalConfirmedQuantityUMB: ExportTaskProcessor.types.number(item.totalConfirmedQuantityUMB || 0),
      totalConfirmedQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      totalConfirmedQuantityUMP: ExportTaskProcessor.types.number(item.totalConfirmedQuantityUMP || 0),
      totalDeliveredQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
      totalDeliveredQuantityUMB: ExportTaskProcessor.types.number(item.totalDeliveredQuantityUMB || 0),
      totalDeliveredQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      totalDeliveredQuantityUMP: ExportTaskProcessor.types.number(item.totalDeliveredQuantityUMP || 0),
      totalInvoicedQuantityUMBUnit: item?.unitOfMeasure?.base?.name,
      totalInvoicedQuantityUMB: ExportTaskProcessor.types.number(item.totalInvoicedQuantityUMB || 0),
      totalInvoicedQuantityUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      totalInvoicedQuantityUMP: ExportTaskProcessor.types.number(item.totalInvoicedQuantityUMP || 0),      

      commercialLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
      commercialLostSaleUMB: ExportTaskProcessor.types.number(item.commercialLostSaleUMB || 0),
      commercialLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      commercialLostSaleUMP: ExportTaskProcessor.types.number(item.commercialLostSaleUMP || 0),
      logisticLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
      logisticLostSaleUMB: ExportTaskProcessor.types.number(item.logisticLostSaleUMB || 0),
      logisticLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      logisticLostSaleUMP: ExportTaskProcessor.types.number(item.logisticLostSaleUMP || 0),
      realLostSaleUMBUnit: item?.unitOfMeasure?.base?.name,
      realLostSaleUMB: ExportTaskProcessor.types.number(item.realLostSaleUMB || 0),
      realLostSaleUMPUnit: item?.unitOfMeasure?.parallel?.name ?? 'UMP',
      realLostSaleUMP: ExportTaskProcessor.types.number(item.realLostSaleUMP || 0),

      sumSoldQuantity: ExportTaskProcessor.types.number(item.sumSoldQuantity || 0),
      sumStockConfirmedERP: ExportTaskProcessor.types.number(item.sumStockConfirmedERP || 0),
      sumDeliveredQuantity: ExportTaskProcessor.types.number(item.sumDeliveredQuantity || 0),
      sumInvoiceQuantity: ExportTaskProcessor.types.number(item.sumInvoiceQuantity || 0),
      lostSaleAmount: ExportTaskProcessor.types.number(item.lostSaleAmount || 0),

      commercialLostSalePercentage: ExportTaskProcessor.types.number(item.commercialLostSalePercentage || 0),
      logisticLostSalePercentage: ExportTaskProcessor.types.number(item.logisticLostSalePercentage || 0),
      realLostSalePercentage: ExportTaskProcessor.types.number(item.realLostSalePercentage || 0),
    }));
  }

  private getLocalizedSummaryProps(): Record<string, string> {
    const currencyCode = this.companyCurrencyPipe.transform('code');
    const suffix = currencyCode ? ` (${currencyCode})` : '';
    return {
      ...LOCALIZED_SUMMARY_PROPERTIES_V2,
      sumSoldQuantity: `Suma Pedido${suffix}`,
      sumStockConfirmedERP: `Suma Reserva${suffix}`,
      sumDeliveredQuantity: `Suma Desp${suffix}`,
      sumInvoiceQuantity: `Suma Factura${suffix}`
    };
  }
}